import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services';
import { getUser } from '../utils/func';
import { codePointCount } from '../utils/contentLimits';
import url from '../utils/url';
import LoadFailure from '../components/Brand/LoadFailure';
import { recoverProtectionDraft, pruneRecoveryJournals } from './recoverDraft';
import './AccountProtection.scss';

export default function AccountProtection({ active, userId }) {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || 'en';
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [appeal, setAppeal] = useState('');
  const [feedback, setFeedback] = useState('');
  const owner = useRef(userId);
  owner.current = active ? userId : null;
  const current = () => owner.current === userId && getUser()?.id === userId;
  useEffect(() => { setAppeal(''); }, [userId]);
  useEffect(() => {
    if (!active || !userId) return undefined;
    owner.current = userId;
    const controller = new AbortController();
    setFailed(false); setResult(null); setFeedback(''); setBusy(false);
    const accountChanged = () => {
      if (getUser()?.id !== userId) { controller.abort(); setResult(null); setAppeal(''); setFeedback(''); }
    };
    window.addEventListener('wiw:session-changed', accountChanged);
    window.addEventListener('storage', accountChanged);
    api.accountProtection(undefined, { signal: controller.signal }).then(value => {
      if (controller.signal.aborted || getUser()?.id !== userId) return;
      // An incomplete/older response is unavailable data, not evidence that
      // this account is restricted. Never turn a missing state into a ban.
      if (!value?.success || !['normal', 'limited', 'quarantined', 'suspended'].includes(value.state)
        || (value.drafts != null && !Array.isArray(value.drafts))) setFailed(true);
      else {
        try { pruneRecoveryJournals(userId, Date.now(), value.drafts || []); } catch { /* The server copy still works without local storage. */ }
        setResult(value);
      }
    }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => {
      controller.abort();
      if (owner.current === userId) owner.current = null;
      window.removeEventListener('wiw:session-changed', accountChanged);
      window.removeEventListener('storage', accountChanged);
    };
  }, [active, userId, attempt]);
  const sendAppeal = async () => {
    if (!current() || busy || !appeal.trim() || codePointCount(appeal) > 2000) return;
    setBusy(true); setFeedback('');
    try {
      const value = await api.accountProtectionAppeal({ message: appeal.trim() });
      if (!current()) return;
      if (!value?.success) throw new Error('Appeal failed');
      setResult(previous => ({ ...previous, appeal_pending: true }));
    } catch { if (current()) setFeedback(t('brand:saveError')); }
    finally { if (current()) setBusy(false); }
  };
  const restore = draft => {
    if (!current()) return;
    try {
      const target = recoverProtectionDraft(userId, draft, result.drafts);
      navigate(url(target.path), { state: target.state });
    } catch (error) {
      setFeedback(t(error.code === 'RECOVERY_DRAFT_EXPIRED' ? 'brand:accountRecoveryExpired' : 'brand:accountRecoveryStorageError', {
        defaultValue: error.code === 'RECOVERY_DRAFT_EXPIRED' ? 'This private draft has expired.'
          : 'There is not enough room to restore this draft on this device. Your server copy is unchanged and remains available until its expiry date.',
      }));
    }
  };
  // This is an actionable notice, not a permanent account-health indicator.
  if ((!result && !failed) || (result?.state === 'normal' && !result.appeal_pending && !result.drafts?.length)) return null;
  return <section className="account-protection" aria-label={t('brand:accountProtection', { defaultValue: 'Account protection' })}>
    <h3>{t('brand:accountProtection', { defaultValue: 'Account protection' })}</h3>
    {!result ? <LoadFailure onRetry={() => setAttempt(value => value + 1)} /> : <>
      {result.state !== 'normal' && <p>{t(`brand:protectionState_${result.state}`, { defaultValue: 'Account activity is temporarily restricted.' })}</p>}
      {result.appeal_pending ? <p role="status">{t('brand:accountAppealPending', { defaultValue: 'Your review request has been received.' })}</p>
        : ['quarantined', 'suspended'].includes(result.state) && <>
          <label htmlFor="account-appeal">{t('brand:accountAppealPrompt', { defaultValue: 'Tell us why this restriction may be a mistake.' })}</label>
          <textarea id="account-appeal" value={appeal} onChange={event => setAppeal(event.target.value)} disabled={busy} />
          <span>{codePointCount(appeal).toLocaleString(language)} / {(2000).toLocaleString(language)}</span>
          <button type="button" disabled={busy || !appeal.trim() || codePointCount(appeal) > 2000} onClick={sendAppeal}>
            {t('brand:accountAppealSend', { defaultValue: 'Request review' })}
          </button>
        </>}
      {result.drafts?.length > 0 && <>
        <p>{t('brand:accountRecoveryInfo', { defaultValue: 'These drafts are private and expire after seven days. Restore text to edit and send it yourself. Check any recordings and reposts again.' })}</p>
        {result.drafts.map(draft => <button key={draft.id} type="button" onClick={() => restore(draft)}>
          {t(draft.kind === 'story' ? 'brand:restoreStoryDraft' : 'brand:restoreCommentDraft', {
            defaultValue: draft.kind === 'story' ? 'Restore event draft' : 'Restore comment draft',
          })} · {new Date(draft.expires_at).toLocaleDateString(language)}
        </button>)}
      </>}
    </>}
    {feedback && <p role="alert">{feedback}</p>}
  </section>;
}
